/**
 * DocsBot AI Integration Utilities
 *
 * Provides helper functions to interact with the DocsBot chatbot
 * that is loaded in the layout.tsx
 *
 * Based on DocsBot Embeddable Widget API:
 * https://docsbot.ai/documentation/embeddable-chat-widget
 */

declare global {
  interface Window {
    DocsBotAI: {
      init: (config: { id: string }) => Promise<void>;
      mount: (config: { id: string }) => Promise<void>;
      open: () => void;
      close: () => void;
      toggle: () => void;
      addUserMessage: (message: string, autoSend?: boolean) => Promise<void>;
    };
  }
}

/**
 * Check if DocsBot is loaded and available
 */
const isDocsBotReady = (): boolean => {
  if (typeof window === 'undefined') return false;
  const api = (window as any).DocsBotAI;
  return !!(api && typeof api.open === 'function' && typeof api.addUserMessage === 'function');
};

/**
 * Wait until DocsBot is available on window or until timeout
 */
export const waitForDocsBotReady = async (timeoutMs: number = 3000, pollIntervalMs: number = 100): Promise<boolean> => {
  if (isDocsBotReady()) return true;
  const start = Date.now();
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (isDocsBotReady()) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start >= timeoutMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, pollIntervalMs);
  });
};

/**
 * Opens the DocsBot chatbot widget
 */
export const openDocsBot = async (): Promise<void> => {
  const ready = isDocsBotReady() || (await waitForDocsBotReady());
  if (!ready) {
    console.warn('DocsBot is not loaded yet. Please try again in a moment.');
    return;
  }

  try {
    window.DocsBotAI.open();
  } catch (error) {
    console.error('Error opening DocsBot:', error);
  }
};

export const askDocsBot = async (message: string, autoSend: boolean = true): Promise<void> => {
  const ready = isDocsBotReady() || (await waitForDocsBotReady());
  if (!ready) {
    console.warn('DocsBot is not loaded yet. Please try again in a moment.');
    return;
  }

  try {
    // Open widget before sending for better UX
    try {
      window.DocsBotAI.open();
    } catch {}
    await window.DocsBotAI.addUserMessage(message, autoSend).catch((error) => {
      console.error('Error sending message to DocsBot:', error);
    });
  } catch (error) {
    console.error('Error asking DocsBot:', error);
  }
};
